import { useMemo } from 'react';
import { Tag } from '@/_models/Tag';
import { DateGroupByType, ListGroupByType, NoteStatus } from '../enums';
import { TNoteGroup, TNote } from '../types';
import { groupNotesByDays } from '../utils';

type Args = {
  notes: TNote[];
  tags: Tag[];
  groupBy: ListGroupByType | DateGroupByType;
};

export const useGroupNotes = ({ tags, notes, groupBy }: Args): TNoteGroup[] => {
  const groupedData = useMemo(() => {
    if (groupBy === DateGroupByType.CreationDate) {
      return groupNotesByDays(notes);
    }

    if (groupBy === ListGroupByType.Status) {
      const completedThreads = notes.filter((note) => note.isCompleted);
      const inProgressThreads = notes.filter((note) => !note.isCompleted);

      const statusGroups: TNoteGroup[] = [];

      if (completedThreads.length > 0) {
        statusGroups.push({
          key: NoteStatus.Completed,
          groupName: 'Complete tasks',
          notes: completedThreads,
        });
      }

      if (inProgressThreads.length > 0) {
        statusGroups.push({
          key: NoteStatus.Incompleted,
          groupName: 'Incomplete tasks',
          notes: inProgressThreads,
        });
      }

      return statusGroups;
    }

    if (groupBy === ListGroupByType.Tags) {
      const threadsByTagId: { [key: string | number]: TNote[] } = {};
      const threadsWithoutTag: TNote[] = [];

      notes.forEach((note) => {
        if (note.tagId !== undefined && note.tagId !== null) {
          if (!threadsByTagId[note.tagId]) {
            threadsByTagId[note.tagId] = [];
          }
          threadsByTagId[note.tagId].push(note);
        } else {
          threadsWithoutTag.push(note);
        }
      });

      const tagGroups: TNoteGroup[] = [];

      tags.forEach((tag) => {
        const threadsForTag = threadsByTagId[tag.id] || [];
        if (threadsForTag.length > 0) {
          tagGroups.push({
            key: tag.id,
            groupName: tag.title,
            notes: threadsForTag,
          });
        }
      });

      if (threadsWithoutTag.length > 0) {
        tagGroups.push({
          key: 'no-tag',
          groupName: 'No Tags Assigned',
          notes: threadsWithoutTag,
        });
      }

      return tagGroups;
    }

    return [];
  }, [groupBy, tags, notes]);

  return groupedData;
};
